import MultiSelectWithCheckbox from 'core/chatbot/components/atoms/MultiSelectWithCheckbox';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createTeacherToolsService } from '../../../../services/teacherToolsService';

type ActivityType = {
  type: string;
  displayName: string;
  enabled: boolean;
};

// Map API type to translation keys in lemonade namespace
const ACTIVITY_TYPE_TRANSLATIONS: Record<string, string> = {
  mcq: 'Multiple choice single',
  mcq_multiple: 'Multiple choice multiple',
  orderlist: 'Classify order list',
  classification: 'Classify classification',
  choicematrix: 'Multiple choice matrix table',
  shorttext: 'Short text',
  clozetext: 'Cloze text',
  clozeassociation: 'Cloze drag and drop',
  association: 'Classify match list',
  linkinglines: 'Classify Linking Lines',
  clozedropdown: 'Cloze dropdown',
};

type ActivityTypeSelectorProps = {
  value: string[];
  onChange: (guids: string[]) => void;
  disabled?: boolean;
  required?: boolean;
};

function ActivityTypeSelector({ value = [], onChange, disabled = false, required = false }: ActivityTypeSelectorProps): JSX.Element {
  const { t } = useTranslation(['chatbot', 'lemonade']);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchActivityTypes = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const teacherToolsService = createTeacherToolsService();
        const response = await teacherToolsService.getActivityTypes(true);
        if (response && Array.isArray(response)) {
          setActivityTypes(response);
        } else if (response && response.data && Array.isArray(response.data)) {
          setActivityTypes(response.data);
        }
      } catch (error) {
        console.error('Error fetching activity types:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivityTypes();
  }, []);

  const options = useMemo(() => {
    return activityTypes.map((activityType) => {
      const translationKey = ACTIVITY_TYPE_TRANSLATIONS[activityType.type];
      const translatedLabel = translationKey ? t(`lemonade:${translationKey}`) : activityType.displayName;
      return {
        value: activityType.type,
        label: translatedLabel || activityType.displayName,
      };
    });
  }, [activityTypes, t]);

  const selectedValue = useMemo(() => {
    if (!value || value.length === 0) return [];
    return options.filter((opt) => value.includes(opt.value));
  }, [value, options]);

  function handleChange(selectedOptions: Array<{ value: string; label: string }> | null): void {
    if (!selectedOptions) {
      onChange([]);
      return;
    }
    const types = selectedOptions.map((opt) => opt.value);
    onChange(types);
  }

  return (
    <div className="activity-type-selector">
      <label className="activity-type-selector__label label">
        {t('chatbot:Activity type')} {required && <sup>*</sup>}
      </label>
      <span className="activity-type-selector__hint">{t('chatbot:You can select multiple templates')}</span>
      <MultiSelectWithCheckbox
        options={options}
        value={selectedValue}
        onChange={handleChange}
        placeholder={t('chatbot:Select activity types')}
        isDisabled={disabled || isLoading}
        isLoading={isLoading}
        maxVisibleValues={1}
        name="activity-type-selector"
      />
    </div>
  );
}

export default ActivityTypeSelector;
